import type { HierarchyNode } from '@bayer-int/portletce-shared';

import type { DatabricksOrder, ViewGroup } from 'portlet/models';

export type InputTypes =
    | 'Select'
    | 'SelectNumber'
    | 'SelectClearable'
    | 'SelectCurrentView'
    | 'MultiSelect'
    | 'RichMultiSelect'
    | 'SelectNumberClearable'
    | 'CheckBox'
    | 'TextInput'
    | 'TextInputWithButton'
    | 'NullableTextInput'
    | 'NumberInput'
    | 'TopN'
    | 'Information'
    | 'Condition'
    | 'Drill'
    | 'AutoDate'
    | 'InputBWQuery'
    | 'HierarchyCustomizationInput'
    | 'Shortcuts'
    | 'DatabricksCondition';

// these types will be supported by setting shortcut buttons
export type SettingInput =
    | string
    | number
    | boolean
    | null
    | DrillDefinitionValue
    | ConditionInputDefinitionValue
    | AutoDateDefinitionValue
    | InputBWQueryDefinitionValue;

export type FieldKey = string;
export interface InputDefinition<T> {
    type: InputTypes;
    key: FieldKey;
    label: string;
    value: T;
    changeable?: boolean;
    scope?: 'user' | 'designer' | 'none';
    /**
     * If defined, and set to 'uninitialized', asynchronous setting definition (if any)
     * will not be loaded until it is transitioned into 'loading' (which may happen based on user intereaction
     * {@link SettingFormDefinition#forceLoadLazySetting} and {@link SettingFormDefinition#forceLoadAllLazySettings}).
     *
     * Using union of literals instead of an boolean so that it may be easily extended
     * with new states when needed (e.g. 'reloading', 'stale', etc).
     *
     * To make setting definition lazy, set the initial value of this field to 'uninitialized'.
     * If you want to handle the loading status in UI, then you also need to transition from
     * 'loading' to 'initialized' after loading the definition values (SettingService#loadSettingDefinition),
     * and respect `lazy` property in respective SettingFormDefinitionInput.
     *
     */
    lazy?: 'uninitialized' | 'loading' | 'initialized';
}

export interface InformationDefinition extends InputDefinition<string> {
    type: 'Information';
}

export interface TextInputWithButtonDefinition extends InputDefinition<string> {
    type: 'TextInputWithButton';
    button: {
        tooltip: string;
        url: string;
    };
}

export interface TopNDefinition extends InputDefinition<string> {
    type: 'TopN';
    select: {
        value: number;
        options: Array<{
            key: number;
            label: string;
        }>;
    };
}
export interface SelectDefinition extends InputDefinition<string> {
    type: 'Select';
    options: Array<{
        key: string;
        label: string;
    }>;
}
export interface SelectNumberDefinition extends InputDefinition<number> {
    type: 'SelectNumber';
    options: Array<{
        key: number;
        label: string;
    }>;
}
export interface SelectClearableDefinition extends InputDefinition<string | null> {
    type: 'SelectClearable';
    options: Array<{
        key: string;
        label: string;
    }>;
}
export interface SelectCurrentViewDefinition extends InputDefinition<number> {
    type: 'SelectCurrentView';
    options: Array<ViewGroup>;
}

export interface MultiSelectDefinition extends InputDefinition<Array<string>> {
    type: 'MultiSelect';
    options: Array<{
        key: string;
        label: string;
    }>;
}
export interface RichMultiSelectDefinition extends InputDefinition<Array<string>> {
    type: 'RichMultiSelect';
    options: Array<{
        key: string;
        label: string;
    }>;
}
export interface SelectNumberClearableDefinition extends InputDefinition<number | null> {
    type: 'SelectNumberClearable';
    options: Array<{
        key: number;
        label: string;
    }>;
}
export interface CheckBoxDefinition extends InputDefinition<boolean> {
    type: 'CheckBox';
}
export interface TextInputDefinition extends InputDefinition<string> {
    type: 'TextInput';
}

export interface NullableTextInputDefinition extends InputDefinition<string | null> {
    type: 'NullableTextInput';
}

export interface NumberInputDefinition extends InputDefinition<number> {
    type: 'NumberInput';
    min?: number;
    max?: number;
    step?: number;
}

export interface HierarchyCustomizationDefinition extends InputDefinition<string[]> {
    type: 'HierarchyCustomizationInput';
    options: HierarchyNode[];
}

type DrillDefinitionValue = Array<{
    key: string;
    order: number;
    /**
     * in case of _normal_ drills this is equal to `key`,
     * for autodrill hierarchies this represents the currently active drill level,
     * whereas `key` is just the identifier of the whole hierarchy
     */
    drill: string;
}>;
export interface DrillDefinition extends InputDefinition<DrillDefinitionValue | null> {
    type: 'Drill';
    allowMultiple: boolean;
    options: Array<{
        key: string;
        label: string;
        hierarchy?: string[];
    }>;
}

export type DatabricksConditionInputDefinitionValue = {
    order: DatabricksOrder;
    field: string;
    limit: number;
};

export interface DatabricksConditionInputDefinition
    extends InputDefinition<DatabricksConditionInputDefinitionValue> {
    type: 'DatabricksCondition';
    condtitionFieldOptions: Array<{ key: string; label: string }>;
    conditionMaxLimit: number;
    conditionAllowChangeOrder: boolean;
}

type ConditionInputDefinitionValue = {
    key: string;
    value: number;
    group?: string;
};
export interface ConditionInputDefinition extends InputDefinition<ConditionInputDefinitionValue> {
    type: 'Condition';
    allowConditionChange: boolean;
    conditionMaxValue: number;
    conditionVariableName: string;
    options: Array<{
        key: string;
        label: string;
        group?: string;
        defaultView?: number | null;
    }>;
}

type AutoDateDefinitionValue = { from: { offset: number }; to: { offset: number } | null };
export interface AutoDateDefinition extends InputDefinition<AutoDateDefinitionValue | null> {
    type: 'AutoDate';
}

type InputBWQueryDefinitionValue = { queryId: string; system: string };
export interface InputBWQueryDefinition
    extends InputDefinition<InputBWQueryDefinitionValue | null> {
    type: 'InputBWQuery';
    systems: string[];
}

export type ShortcutElement = { key: string; label: string; value: SettingInput };

export interface ShortcutsDefinition
    extends InputDefinition<{
        left: Array<ShortcutElement>;
        center: Array<ShortcutElement>;
        right: Array<ShortcutElement>;
    }> {
    type: 'Shortcuts';
}

export type SettingFormInputDefinition =
    | InformationDefinition
    | TextInputWithButtonDefinition
    | SelectDefinition
    | SelectNumberDefinition
    | SelectCurrentViewDefinition
    | MultiSelectDefinition
    | RichMultiSelectDefinition
    | SelectClearableDefinition
    | SelectNumberClearableDefinition
    | CheckBoxDefinition
    | TextInputDefinition
    | NullableTextInputDefinition
    | NumberInputDefinition
    | TopNDefinition
    | ConditionInputDefinition
    | DrillDefinition
    | AutoDateDefinition
    | InputBWQueryDefinition
    | HierarchyCustomizationDefinition
    | ShortcutsDefinition
    | DatabricksConditionInputDefinition;

export const SettingFormInputDefinition = {
    tryApplySettingValueToNewDefinition: (
        oldDefinition: SettingFormInputDefinition,
        value: SettingFormInputDefinition['value'],
        newDefinition: SettingFormInputDefinition,
    ): SettingFormInputDefinition['value'] | undefined => {
        if (oldDefinition.type !== newDefinition.type) return undefined;

        switch (newDefinition.type) {
            case 'CheckBox':
            case 'TextInput':
            case 'NullableTextInput':
            case 'NumberInput':
                return value;
            case 'Select':
                return newDefinition.options.some(option => option.key === value)
                    ? value
                    : undefined;
            case 'SelectClearable':
                return newDefinition.options.some(option => option.key === value) || value === null
                    ? value
                    : undefined;
            case 'SelectNumber':
                return newDefinition.options.some(option => option.key === value)
                    ? value
                    : undefined;
            case 'SelectNumberClearable':
                return newDefinition.options.some(option => option.key === value) || value === null
                    ? value
                    : undefined;
            case 'MultiSelect':
            case 'RichMultiSelect':
                if (oldDefinition.type !== newDefinition.type) return undefined;

                return oldDefinition.value.every(value =>
                    newDefinition.options.map(option => option.key).includes(value),
                )
                    ? value
                    : undefined;
            default:
                return undefined;
        }
    },
};

export type SettingFormInputValue = Pick<SettingFormInputDefinition, 'key' | 'value'>;

export interface SettingFormDefinition {
    fields: {
        userSettingFields: Array<SettingFormInputDefinition>;
        designerSettingFields: Array<SettingFormInputDefinition>;
        technicalInformation: Array<SettingFormInputDefinition>;
    };
    onChange: (field: SettingFormInputDefinition) => void;
    forceLoadLazySetting: (field: SettingFormInputDefinition) => void;
    forceLoadAllLazySettings: () => void;
    onSubmit: () => void;
    onClose: () => void;
}
